import { Base } from '@studiometa/js-toolkit';
import { focusTrap, nextFrame, toggleClass } from '@studiometa/js-toolkit/utils';
import MobileAccordion from '#js/molecules/MobileAccordion.js';

const { trap, untrap } = focusTrap();

/**
 * Test if the given element has a transition duration.
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function testTransition(element) {
  const { transitionDuration } = window.getComputedStyle(element);
  return !!transitionDuration && transitionDuration !== '0s';
}

/**
 * Header component class
 */
export default class Header extends Base {
  static config = {
    name: 'Header',
    refs: ['menu', 'toggle', 'openLabel', 'closeLabel'],
    options: {
      open: Boolean,
    },
    components: {
      MobileAccordion,
    },
  };

  isMoving = false;

  /**
   * Open
   * @returns {Promise}
   */
  open() {
    return new Promise((resolve) => {
      if (this.$options.open || this.isMoving) {
        resolve();
        return;
      }

      this.isMoving = true;

      this.$refs.menu.style.visibility = '';
      this.$refs.menu.setAttribute('aria-hidden', 'false');
      this.$options.open = true;

      if (testTransition(this.$refs.menu)) {
        const transitionendHandler = () => {
          this.$refs.menu.removeEventListener('transitionend', transitionendHandler, false);
          this.isMoving = false;
          resolve();
        };
        this.$refs.menu.addEventListener('transitionend', transitionendHandler, false);
      }

      toggleClass(document.documentElement, 'is-menu-open', true);

      this.$refs.openLabel.setAttribute('aria-hidden', 'true');
      this.$refs.closeLabel.setAttribute('aria-hidden', 'false');
    });
  }

  /**
   * Close
   * @returns {Promise}
   */
  close() {
    return new Promise((resolve) => {
      if (!this.$options.open || this.isMoving) {
        resolve();
        return;
      }

      this.isMoving = true;
      this.$refs.menu.style.transition = 'none';

      nextFrame(() => {
        this.$refs.menu.style.transition = '';

        this.$refs.menu.setAttribute('aria-hidden', 'true');

        if (testTransition(this.$refs.menu)) {
          const transitionendHandler = () => {
            this.$refs.menu.removeEventListener('transitionend', transitionendHandler, false);
            nextFrame(() => {
              this.$refs.menu.style.visibility = 'hidden';
              this.$options.open = false;
              this.isMoving = false;
              resolve();
            });
          };
          this.$refs.menu.addEventListener('transitionend', transitionendHandler, false);
        }

        toggleClass(document.documentElement, 'is-menu-open', false);

        const accordionItem = this.$children?.MobileAccordion?.[0]?.$children?.AccordionItem?.[0];

        if (accordionItem) {
          accordionItem.close();
        }

        untrap();
        this.$refs.openLabel.setAttribute('aria-hidden', 'false');
        this.$refs.closeLabel.setAttribute('aria-hidden', 'true');
      });
    });
  }

  /**
   * Toggle
   * @returns {Promise}
   */
  toggle() {
    return this.$options.open ? this.close() : this.open();
  }

  /**
   * Btn click
   */
  onToggleClick() {
    this.toggle();
  }

  /**
   * Keyboard management.
   * @param {object} props
   * @param {KeyboardEvent} props.event
   * @param {boolean} props.ESC
   * @param {boolean} props.isDown
   * @param {boolean} props.isUp
   */
  keyed({ event, ESC, isDown, isUp }) {
    if (!this.$options.open) {
      return;
    }

    if (isDown) {
      trap(this.$el, event);
    }

    if (isUp && ESC) {
      this.close();
    }
  }

  /**
   * Scrolled
   * @param {object} props
   * @param {number} props.y
   * @param {object} props.direction
   */
  scrolled({ y, direction }) {
    const isOnTop = y <= 0;
    const isNearTop = y < 96;
    const isOnBottom =
      y >= document.documentElement.offsetHeight - window.innerHeight - 1 && y !== 0;

    toggleClass(document.documentElement, 'is-on-top', isOnTop);
    toggleClass(document.documentElement, 'is-near-top', isNearTop);
    toggleClass(document.documentElement, 'is-on-bottom', isOnBottom);

    if (isOnBottom && document.querySelector('footer')) {
      const navbar = this.$root.$children.Navbar[0];
      navbar?.show();
    }

    if (direction.y === 'NONE') {
      return;
    }

    toggleClass(document.documentElement, 'is-scrolling-down', direction.y === 'DOWN');
    toggleClass(document.documentElement, 'is-scrolling-up', direction.y === 'UP');
    toggleClass(document.documentElement, 'is-hero-visible', !!this.$root.$refs.hero);
  }

  /**
   * Resized
   */
  resized() {
    this.scrolled({
      y: window.scrollY,
      direction: {
        y: 'DOWN',
      },
    });
  }

  /**
   * Mounted
   */
  mounted() {
    if (!this.$refs.menu || !this.$refs.toggle) {
      this.$destroy();
      return;
    }
    this.$refs.toggle.setAttribute('aria-controls', this.$id);
    this.$refs.menu.setAttribute('id', this.$id);
    this.scrolled({
      y: window.scrollY,
      direction: {
        y: 'DOWN',
      },
    });
  }
}
